import { normalizePiece } from "@/components/CountertopDesigner";

export const uid = () => Math.random().toString(36).slice(2, 10);
export const r2 = (n) => Math.round(n * 10000) / 10000;
export const LETTER = (i) => String.fromCharCode(65 + (i % 26)) + (i >= 26 ? Math.floor(i / 26) : "");

export const polyArea = (pts) =>
  Math.abs(
    pts.reduce((s, [x, y], i) => {
      const [x2, y2] = pts[(i + 1) % pts.length];
      return s + x * y2 - x2 * y;
    }, 0) / 2,
  );
export const edgeLen = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
};
export const edgeMid = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
};
// unit normal pointing away from the polygon
export const outNormal = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const L = edgeLen(pts, i) || 1;
  let n = [(b[1] - a[1]) / L, -(b[0] - a[0]) / L];
  const m = edgeMid(pts, i);
  if (insidePoly([m[0] + n[0] * 0.5, m[1] + n[1] * 0.5], pts)) n = [-n[0], -n[1]];
  return n;
};
export const insidePoly = ([x, y], pts) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};
export const distToSeg = ([px, py], [ax, ay], [bx, by]) => {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
};

export const absPts = (p) => p.points.map(([x, y]) => [r2(x + p.x), r2(y + p.y)]);

// Rebuild a piece from absolute points, keeping edge meta by index.
export const fromAbs = (piece, abs, edges) => {
  const minx = Math.min(...abs.map((p) => p[0]));
  const miny = Math.min(...abs.map((p) => p[1]));
  const pts = abs.map(([x, y]) => [r2(x - minx), r2(y - miny)]);
  const dx = minx - piece.x;
  const dy = miny - piece.y;
  return normalizePiece({
    ...piece,
    points: pts,
    edges: edges || pts.map((_, i) => piece.edges?.[i] || { profile: "Eased", seam: false }),
    x: r2(minx),
    y: r2(miny),
    cutouts: (piece.cutouts || []).map((c) => ({ ...c, x: r2((c.x || 0) - dx), y: r2((c.y || 0) - dy) })),
  });
};

// Set side i to exactly L inches by sliding the next two corners along the side.
// A locked neighbour makes us slide the previous two corners instead; both locked → null.
export const setSideLength = (piece, i, L) => {
  const pts = piece.points.map((p) => [...p]);
  const n = pts.length;
  const a = pts[i];
  const b = pts[(i + 1) % n];
  const cur = edgeLen(pts, i);
  if (cur < 0.01 || L <= 0.01) return piece;
  const ux = (b[0] - a[0]) / cur;
  const uy = (b[1] - a[1]) / cur;
  const d = L - cur;
  const locked = (k) => !!piece.edges?.[((k % n) + n) % n]?.locked;
  let move;
  let sign;
  if (!locked(i + 2)) {
    move = [(i + 1) % n, (i + 2) % n];
    sign = 1;
  } else if (!locked(i - 2)) {
    move = [i, (i - 1 + n) % n];
    sign = -1;
  } else return null;
  move.forEach((k) => {
    pts[k] = [r2(pts[k][0] + ux * d * sign), r2(pts[k][1] + uy * d * sign)];
  });
  return fromAbs(piece, pts.map(([x, y]) => [x + piece.x, y + piece.y]), piece.edges);
};

export const snapTo = (v, step) => r2(Math.round(v / step) * step);

// Ortho / 45° constraint of `pt` relative to `from`.
export const constrain = (from, pt, mode) => {
  if (!from || !mode) return pt;
  const dx = pt[0] - from[0];
  const dy = pt[1] - from[1];
  if (mode === 90) return Math.abs(dx) >= Math.abs(dy) ? [pt[0], from[1]] : [from[0], pt[1]];
  const ang = Math.atan2(dy, dx);
  const q = Math.round(ang / (Math.PI / 4)) * (Math.PI / 4);
  const L = Math.hypot(dx, dy);
  return [r2(from[0] + Math.cos(q) * L), r2(from[1] + Math.sin(q) * L)];
};

export const dropCollinear = (pts) =>
  pts.filter((p, i, arr) => {
    const a = arr[(i - 1 + arr.length) % arr.length];
    const b = arr[(i + 1) % arr.length];
    const cross = (p[0] - a[0]) * (b[1] - a[1]) - (p[1] - a[1]) * (b[0] - a[0]);
    return Math.abs(cross) > 0.01;
  });

export const fmtIn = (n) => {
  const v = Math.round(n * 16) / 16;
  const whole = Math.floor(v);
  const frac = v - whole;
  const F = { 0.5: "½", 0.25: "¼", 0.75: "¾", 0.125: "⅛", 0.375: "⅜", 0.625: "⅝", 0.875: "⅞" };
  if (frac === 0) return `${whole}`;
  return F[frac] ? `${whole || ""}${F[frac]}` : v.toFixed(2);
};

export const profileAbbr = (e) => {
  if (!e) return "";
  const p = e.profile || "Eased";
  if (p === "Unfinished") return "WALL";
  const abbr = p
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 4);
  const bs = Number(e.backsplash_height_in) > 0 ? ` +BS${fmtIn(e.backsplash_height_in)}` : "";
  return `${p.length <= 6 ? p.toUpperCase() : abbr}${bs}${e.seam ? " · SEAM" : ""}`;
};

export const pieceSqft = (p) => polyArea(p.points) / 144;
export const finishedLf = (p) =>
  (p.edges || []).reduce(
    (s, e, i) => s + ((e.profile || "Eased") !== "Unfinished" && !e.seam ? edgeLen(p.points, i) : 0),
    0,
  ) / 12;
